// Accounting's lists and month end, as zod (pages/ux-full screens/accounting.js: Invoices, Payments and
// refunds, Close a month; components/money.js: CloseChecklist). Each is exactly its action's answer
// (invoices.list, payments.list, periods.closeChecks), so each can become a chat view as it is. None is
// in TOOL_VIEWS yet.
import { z } from 'zod';

const Id = z.string().min(1);
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const Money = z.number().int();

/** Where an invoice stands, as invoices.list works it out (one each). */
export const INVOICE_STANDINGS = ['draft', 'sent', 'part_paid', 'overdue', 'paid', 'void'] as const;

/** invoices.list: a page of invoices, newest first, and what the whole filter adds up to. */
export const InvoiceListProps = z.object({
  rows: z.array(
    z.object({
      id: Id,
      number: z.string(),
      status: z.enum(['draft', 'finalized', 'sent', 'partially_paid', 'paid', 'void']),
      standing: z.enum(INVOICE_STANDINGS),
      customerId: Id,
      customerName: z.string(),
      jobId: Id.nullable(),
      jobNumber: z.string().nullable(),
      issuedOn: LocalDate.nullable(),
      dueOn: LocalDate.nullable(),
      daysLate: z.number().int(),
      totalCents: Money,
      balanceCents: Money,
    }),
  ),
  totals: z.object({
    count: z.number().int(),
    totalCents: Money,
    owedCents: Money,
    overdueCents: Money,
  }),
  next: z.string().nullable(),
});
export type InvoiceListProps = z.infer<typeof InvoiceListProps>;
export type InvoiceListRow = InvoiceListProps['rows'][number];

/** payments.list: a page of payments, newest first, and what the whole filter adds up to. */
export const PaymentListProps = z.object({
  rows: z.array(
    z.object({
      id: Id,
      receivedAt: z.string(),
      receivedOn: LocalDate,
      customerId: Id,
      customerName: z.string(),
      takenBy: z.string().nullable(),
      method: z.enum(['cash', 'cheque', 'etransfer', 'card', 'other', 'financing']),
      reference: z.string().nullable(),
      amountCents: Money,
      status: z.enum(['recorded', 'disputed', 'chargeback_lost', 'void']),
      deposited: z.boolean(),
      invoices: z.array(z.object({ id: Id, number: z.string() })),
      creditCents: Money,
      refundedCents: Money,
      refundReason: z.string().nullable(),
    }),
  ),
  totals: z.object({ count: z.number().int(), inCents: Money, refundedCents: Money }),
  next: z.string().nullable(),
});
export type PaymentListProps = z.infer<typeof PaymentListProps>;
export type PaymentListRow = PaymentListProps['rows'][number];

/** periods.closeChecks: a month's checks before it closes, and whether it can. */
export const CloseChecksProps = z.object({
  period: z.object({
    id: Id,
    startDate: LocalDate,
    endDate: LocalDate,
    status: z.enum(['open', 'closed']),
    closedAt: z.string().nullable(),
    closedBy: z.string().nullable(),
  }),
  checks: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      ok: z.boolean(),
      /** One that stops the close; the others are only to look at. */
      blocking: z.boolean(),
      detail: z.string().nullable(),
    }),
  ),
  ready: z.boolean(),
  /** Anything posted in the month: one with nothing in it has nothing to close. */
  posted: z.boolean(),
});
export type CloseChecksProps = z.infer<typeof CloseChecksProps>;
